import React from 'react';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { redirect as RouteRedirect, RouterProvider } from 'react-router-dom';

// @Redux
import { useSelector } from 'react-redux';
import { RootState } from './redux/reducers';
import { useAppDispatch } from './redux/store';
import { getCountries } from './redux/slices/countries/actions-async';
import { getUser } from './redux/slices/user/ActionsAsync';
import OneSignal from 'react-onesignal';

// @Routes
import { Router } from './router/router';
import { socket } from './utils';

export const App = () => {
  const { user } = useSelector((state: RootState) => state);
  const dispatch = useAppDispatch();
  const { router } = Router();
  const { i18n } = useTranslation();

  React.useEffect(() => {
    dispatch(getCountries());

    const urlLang = new URLSearchParams(window.location.search).get('lang');
    const language = urlLang || user?.language || window.localStorage.getItem('lang') || 'es';

    window.localStorage.setItem('lang', language);
    i18n.changeLanguage(language);

    socket.on('connect', () => {
      if (user.isAuthenticated) {
        socket.emit('joinUserId', user.user?.id.toString());
      }
    });
    return () => {
      socket.on('disconnect', () => {});
    };
  }, [user.language, i18n]);

  React.useEffect(() => {
    if (user.isAuthenticated && user.user?.email && user.user?.id) {
      dispatch(
        getUser({
          email: user.user.email,
          idSystem: user.user.id,
        }),
      );
      RouteRedirect('/');
    }
  }, [user.isAuthenticated, dispatch]);

  const initializeOneSignal = async () => {
    await OneSignal.init({
      appId: process.env.REACT_APP_ONESIGNAL_APP_ID || '',
      notifyButton: {
        enable: true,
        size: 'medium',
        position: 'bottom-right',
        prenotify: true,
        showCredit: false,
        text: {
          'dialog.main.title': "We'd like to show you notifications for the latest updates.",
          'dialog.main.button.subscribe': 'Allow',
          'dialog.main.button.unsubscribe': 'No Thanks',
          'dialog.blocked.message': 'You have blocked notifications.',
          'dialog.blocked.title': 'Notifications Blocked',
          'message.action.resubscribed': 'You have resubscribed to notifications.',
          'message.action.subscribed': 'You have subscribed to notifications.',
          'message.action.unsubscribed': 'You have unsubscribed from notifications.',
          'message.action.subscribing': 'Subscribing to notifications...',
          'message.prenotify': 'Click to enable notifications.',
          'tip.state.subscribed': 'You are subscribed to notifications.',
          'tip.state.unsubscribed': 'You are not subscribed to notifications.',
          'tip.state.blocked': 'Notifications are blocked.',
        },
      },
    });
  };

  // Inicializar OneSignal solo una vez
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      initializeOneSignal();
    }
  }, []); // Solo se ejecuta una vez al montar el componente

  // Configurar usuario cuando esté autenticado y OneSignal esté inicializado
  React.useEffect(() => {
    const configureOneSignalUser = async () => {
      if (user.isAuthenticated && user.user) {
        try {
          // Verificar si OneSignal está realmente inicializado
          if (typeof OneSignal.Slidedown === 'undefined') {
            return;
          }
          await OneSignal.Slidedown.promptPush();

          // Login con external user ID único (agregar prefijo para evitar conflictos)
          if (user.idCRM) {
            const uniqueExternalId = `cml_user_${user.idCRM}`;
            await OneSignal.login(uniqueExternalId);
            if (OneSignal.User.externalId === uniqueExternalId) {
              await OneSignal.User.PushSubscription.optIn();
            }
          }
        } catch (error) {
          // Intentar con un ID alternativo si falla
          if (user.user.id) {
            try {
              const fallbackId = `cml_${user.user.email?.replace('@', '_').replace('.', '_')}_${user.user.id}`;
              await OneSignal.login(fallbackId);
            } catch (fallbackError) {
              console.error('Fallback OneSignal login also failed:', fallbackError);
            }
          }
        }
      }
    };

    configureOneSignalUser();
  }, [user]);

  return (
    <>
      <Toaster />
      <RouterProvider router={router} />
    </>
  );
};
